/**
 * Resolve an AI fact's stored provenance (`MessageFact.sourceRefs`) to
 * the ids of the source cards it was grounded in (#500).
 *
 * Hovering a fact highlights the source it came from, so the host needs to turn
 * the fact's provenance payload into the `highlightedReferenceIds` set the
 * references panel already honours. Shared so Neptune's ProposedAnswerTab and
 * Venus's AgentPage cannot resolve the same fact to different cards.
 *
 * The matching rule mirrors the backend's, documented in
 * `ai/agents/proposed_answer/nodes/fact_edit_gate.py` ("Fact -> source matching
 * rule"), with one shortcut in front: a ref's `source_id` is already the durable
 * MessageSource id (resolved at generation time by `_resolve_card_id`), which is
 * the same id the frontend card carries as `AssistantReference.id`. So per ref:
 *
 *   0. `source_id` matches a reference's `id`                  -> that card
 *   1. else `qdrant_source_id` matches a reference's `sourceId` -> that chunk's card
 *   2. else `source_document_id` matches `sourceDocumentId`     -> document level,
 *      so a re-chunked pool whose chunk id shifted still resolves to the document
 *   3. else `source_conversation_message_id` matches
 *      `sourceConversationMessageId`                            -> past-message card
 *
 * The tiers are per-ref fallbacks: a tier that matches at least one card ends the
 * search for that ref. A ref matching nothing contributes no id — the source it
 * names is not on screen (deselected, or never pooled), and the caller renders no
 * highlight rather than a wrong one.
 *
 * A fact can be grounded in several sources, so the result is a set and several
 * cards can highlight at once.
 */

/** One entry of a fact's `sourceRefs`. Snake_case: it is a raw JSON payload
 *  persisted by the pipeline, not a camelCased GraphQL field. Only the identity
 *  fields used for matching are declared; entries also carry display fields
 *  (title, excerpt, page number) this resolver has no use for. */
export interface FactSourceRef {
  /** Durable MessageSource id of the card this ref lines up with. Null when the
   *  answer persisted no card for the cited source. */
  source_id?: string | null;
  /** The source's own Qdrant id (the cited chunk). */
  qdrant_source_id?: string | null;
  source_document_id?: string | null;
  source_conversation_message_id?: string | null;
  /** Machine scope, stamped by the retrieval scope engine at generation time. Set
   *  only for a source judged to be about a different machine type than the ticket. */
  scope_expected_label?: string | null;
  scope_source_label?: string | null;
  /** The cross-cutting property the source gets wrong ("Steuerungsplattform"), set
   *  independently of the machine type. */
  scope_fact_label?: string | null;
}

/** What a fact's marker says, or null when the fact carries none. */
export interface FactMachineScope {
  /** Null when every backing source is off on a cross-cutting fact only, with the
   *  machine type itself matching or unjudgeable. */
  expectedLabel: string | null;
  /** Null when the backing sources name different types, or none nameable. */
  sourceLabel: string | null;
  /** The cross-cutting property, when that is what every source gets wrong. */
  factLabel: string | null;
}

/**
 * The marker for a fact, derived from the sources it was written from.
 *
 * A fact is marked only when EVERY backing source is off-type. One on-type source is
 * enough to have grounded the claim in the customer's own machine, and marking the
 * fact anyway would spend the operator's attention on a caveat that does not apply —
 * the opposite of what "ink marks the exception" is for.
 *
 * A fact with no provenance (human-typed, or written before provenance was recorded)
 * is never marked: nothing was judged, so there is nothing to caveat.
 */
export function factMachineScope(
  sourceRefs: readonly FactSourceRef[] | null | undefined,
): FactMachineScope | null {
  const refs = (sourceRefs ?? []).filter(
    (r): r is FactSourceRef => r != null && typeof r === "object",
  );
  if (!refs.length) return null;
  // Off-type OR off-fact: either is a caveat the operator needs, and a fact violation
  // can hold with the machine type matching, so requiring the type label would drop it.
  if (!refs.every((r) => !!r.scope_expected_label || !!r.scope_fact_label)) return null;

  const sourceLabels = new Set(refs.map((r) => r.scope_source_label ?? ""));
  const factLabels = new Set(refs.map((r) => r.scope_fact_label ?? ""));
  return {
    expectedLabel: refs[0].scope_expected_label
      ? String(refs[0].scope_expected_label)
      : null,
    factLabel:
      factLabels.size === 1 && refs[0].scope_fact_label
        ? String(refs[0].scope_fact_label)
        : null,
    // Name the type only when the sources agree on one; a fact drawn from two
    // different off-type machines has no single type to point at.
    sourceLabel:
      sourceLabels.size === 1 && refs[0].scope_source_label
        ? String(refs[0].scope_source_label)
        : null,
  };
}

/** Minimal source-card shape needed to match. Structurally satisfied by
 *  `AssistantReference`. */
export interface ReferenceForFactSource {
  id: string;
  sourceId?: string | null;
  sourceDocumentId?: string | null;
  sourceConversationMessageId?: string | null;
}

/** Compare a JSON-sourced ref value against a reference field. The ref payload
 *  is untyped JSON, so a uuid can arrive as a non-string; normalize both sides. */
function sameId(
  refValue: string | null | undefined,
  cardValue: string | null | undefined,
): boolean {
  if (refValue == null || cardValue == null) return false;
  return String(refValue) === String(cardValue);
}

export function factSourceReferenceIds(
  sourceRefs: readonly FactSourceRef[] | null | undefined,
  references: readonly ReferenceForFactSource[] | null | undefined,
): Set<string> {
  const ids = new Set<string>();
  if (!sourceRefs?.length || !references?.length) return ids;

  for (const ref of sourceRefs) {
    if (ref == null || typeof ref !== "object") continue;

    // Tier 0: the pre-resolved card id.
    const byCardId = references.filter((r) => sameId(ref.source_id, r.id));
    if (byCardId.length > 0) {
      for (const r of byCardId) ids.add(r.id);
      continue;
    }

    // Tier 1: exact cited chunk.
    const byChunk = references.filter((r) => sameId(ref.qdrant_source_id, r.sourceId));
    if (byChunk.length > 0) {
      for (const r of byChunk) ids.add(r.id);
      continue;
    }

    // Tier 2: document level — every card drawn from the same document.
    const byDocument = references.filter((r) =>
      sameId(ref.source_document_id, r.sourceDocumentId),
    );
    if (byDocument.length > 0) {
      for (const r of byDocument) ids.add(r.id);
      continue;
    }

    // Tier 3: past-message source.
    const byMessage = references.filter((r) =>
      sameId(ref.source_conversation_message_id, r.sourceConversationMessageId),
    );
    for (const r of byMessage) ids.add(r.id);
  }

  return ids;
}

/** Minimal fact shape needed to build the reverse map. Structurally satisfied
 *  by `MessageFact`. */
export interface FactForSourceReverse {
  id: string;
  sourceRefs?: readonly FactSourceRef[] | null;
}

/**
 * Inverse of {@link factSourceReferenceIds}: source-card id -> the ids of the
 * facts grounded in that card. Lets a host highlight the facts written from a
 * source when the operator hovers that source (the reverse of the fact-hover
 * highlight). Built from the same resolver so both directions agree on which
 * fact ties to which card.
 */
export function factIdsBySourceReference(
  facts: readonly FactForSourceReverse[] | null | undefined,
  references: readonly ReferenceForFactSource[] | null | undefined,
): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  if (!facts?.length || !references?.length) return map;

  for (const fact of facts) {
    if (!fact?.id) continue;
    for (const sourceId of factSourceReferenceIds(fact.sourceRefs, references)) {
      let bucket = map.get(sourceId);
      if (!bucket) {
        bucket = new Set<string>();
        map.set(sourceId, bucket);
      }
      bucket.add(fact.id);
    }
  }

  return map;
}
